import { Loader } from 'components/ai-elements/loader';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs';
import head from 'lodash/head';
import isNil from 'lodash/isNil';
import map from 'lodash/map';
import take from 'lodash/take';
import { isExcelFile } from 'pages/Chat/utils/artifact-utils';
import Papa from 'papaparse';
import React, { useEffect, useState } from 'react';
import { captureArtifactError } from 'sentry/artifact';

const MAX_ROWS = 500;
const MAX_COLS = 50;
// Don't attempt to parse very large files in the browser — the artifact
// header's Download button is the escape hatch.
const MAX_PREVIEW_BYTES = 20 * 1024 * 1024;

type Sheet = {
  name: string;
  /** First row is treated as the header row. */
  rows: unknown[][];
};

const parseCsv = (text: string): Sheet[] => {
  // papaparse auto-detects the delimiter, which covers TSV with the same
  // code path. 'greedy' also drops rows of only whitespace.
  const result = Papa.parse<unknown[]>(text, { skipEmptyLines: 'greedy' });
  return [{ name: 'Sheet 1', rows: result.data }];
};

const parseExcel = async (buffer: ArrayBuffer): Promise<Sheet[]> => {
  // Dynamic import keeps SheetJS (~140KB gzip) out of the main bundle —
  // it loads only when an Excel artifact is actually opened.
  const XLSX = await import('xlsx');
  const workbook = XLSX.read(buffer);
  return map(workbook.SheetNames, (name) => ({
    name,
    // header: 1 returns arrays-of-arrays instead of keyed objects, matching
    // the CSV shape so both formats share the same table renderer.
    rows: XLSX.utils.sheet_to_json<unknown[]>(workbook.Sheets[name], { header: 1 }),
  }));
};

const formatCell = (value: unknown): string => {
  if (isNil(value)) return '';
  return String(value);
};

const SheetTable = ({ sheet }: { sheet: Sheet }) => {
  const headerRow = head(sheet.rows) || [];
  const dataRows = take(sheet.rows.slice(1), MAX_ROWS);
  const clippedCols = headerRow.length > MAX_COLS;
  const shownHeader = take(headerRow, MAX_COLS);
  const totalDataRows = sheet.rows.length - 1;

  return (
    <div className="overflow-auto rounded-md border border-border">
      <Table>
        <TableHeader className="bg-background sticky top-0 z-10">
          <TableRow className="h-7">
            {map(shownHeader, (cell, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <TableHead key={i} className="py-1 px-2 text-xs whitespace-nowrap">
                {formatCell(cell)}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {map(dataRows, (row, rowIdx) => (
            // eslint-disable-next-line react/no-array-index-key
            <TableRow key={rowIdx} className="h-7">
              {map(take(row, MAX_COLS), (cell, colIdx) => (
                // eslint-disable-next-line react/no-array-index-key
                <TableCell key={colIdx} className="py-1 px-2 text-xs whitespace-nowrap">
                  {formatCell(cell)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {(totalDataRows > MAX_ROWS || clippedCols) && (
        <p className="border-t border-border px-2 py-1 text-xs text-muted-foreground">
          {totalDataRows > MAX_ROWS
            && `Showing first ${MAX_ROWS} of ${totalDataRows} rows. `}
          {clippedCols && `Showing first ${MAX_COLS} of ${headerRow.length} columns. `}
          Download the file for the full data.
        </p>
      )}
    </div>
  );
};

type TableViewerProps = {
  url: string;
  mediaType: string | undefined;
};

/**
 * Best-effort table preview for CSV/TSV and Excel artifacts. Multi-sheet
 * workbooks render with sheet tabs; large files fall back to download-only.
 */
export const TableViewer = ({ url, mediaType }: TableViewerProps) => {
  const [sheets, setSheets] = useState<Sheet[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tooLarge, setTooLarge] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Failed to load table: ${res.status}`);
      const contentLength = Number(res.headers.get('content-length') || 0);
      if (contentLength > MAX_PREVIEW_BYTES) {
        setTooLarge(true);
        return;
      }
      const parsed = isExcelFile(mediaType, url)
        ? await parseExcel(await res.arrayBuffer())
        : parseCsv(await res.text());
      if (!cancelled) setSheets(parsed);
    };

    load().catch((err) => {
      if (cancelled) return;
      captureArtifactError(err, { url, kind: 'table' });
      setError(err.message);
    });

    return () => {
      cancelled = true;
    };
  }, [url, mediaType]);

  if (error) {
    return (
      <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
        {error}
      </div>
    );
  }

  if (tooLarge) {
    return (
      <div className="rounded-lg border border-border bg-muted/30 p-4 text-sm text-muted-foreground">
        This file is too large to preview — use the Download button to save it.
      </div>
    );
  }

  if (sheets === null) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader />
      </div>
    );
  }

  if (sheets.length === 0 || (sheets.length === 1 && sheets[0].rows.length === 0)) {
    return (
      <div className="rounded-lg border border-border bg-muted/30 p-4 text-sm text-muted-foreground">
        This file contains no rows.
      </div>
    );
  }

  if (sheets.length === 1) {
    return <SheetTable sheet={sheets[0]} />;
  }

  return (
    <Tabs defaultValue={sheets[0].name}>
      <TabsList>
        {map(sheets, (sheet) => (
          <TabsTrigger key={sheet.name} value={sheet.name}>
            {sheet.name}
          </TabsTrigger>
        ))}
      </TabsList>
      {map(sheets, (sheet) => (
        <TabsContent key={sheet.name} value={sheet.name}>
          <SheetTable sheet={sheet} />
        </TabsContent>
      ))}
    </Tabs>
  );
};
